import { fieldsOf, flag, foreignMessage, foreignValue, isPlainRecord, listOf, name, oneOf, report, whole, type Place } from "./checks.ts";
import type { RulesProblem } from "./types.ts";

/**
 * The looks table's record check (ADR-094 decisions 1, 18 and 19): a piece of a layered character, as text about the
 * packs and never their art. A record holds its `key` (the pack's code and colour), its `slot`, its `style` and
 * `colour` (every colour of every item is a piece of its own, and the key is the two joined), its `label`, the combat
 * `sets` it has every animation of, and `creator`, whether a player may choose it.
 *
 * The table is append-only: a piece's number on the wire is its place counting from 1, so a piece is never inserted,
 * reordered, renamed or removed, and a withdrawn piece becomes `creator: false` and keeps its key and its number. This
 * check cannot see the table that came before, so that rule is held by a golden test of the shipped keys' order.
 *
 * The character sprite catalogue (`sprite/look-catalogue.ts`) is another thing: the converter writes it from the packs
 * with every item, every colour and every animation's frames, and it stays in the private store. This table names the
 * subset of its items the game ships, by the same codes and colour numbers, and states each one's `sets`, which a
 * test over the private catalogue proves. Neither holds a pixel.
 */

/**
 * Where a piece is worn. `skin`, `hair` and `outfit` are what an appearance chooses; `hat`, `mainHand` and `offHand`
 * come from what a character has on.
 */
export const LOOK_SLOTS = ["skin", "hair", "outfit", "hat", "mainHand", "offHand"] as const;
export type LookSlot = (typeof LOOK_SLOTS)[number];

/**
 * The slot of the character sprite catalogue (`sprite/look-catalogue.ts`) that holds each look slot's items: the
 * catalogue the converter writes names the skin `body` and the hands `weapon` and `shield`. A piece's `style` is the
 * catalogue item's code and its `colour` the item's variant.
 */
export const CATALOGUE_SLOT_OF: Readonly<Record<LookSlot, string>> = Object.freeze({ skin: "body", hair: "hair", outfit: "outfit", hat: "hat", mainHand: "weapon", offHand: "shield" });

/**
 * The slots a player chooses a piece for: the three an appearance names.
 */
export const APPEARANCE_SLOTS: readonly LookSlot[] = ["skin", "hair", "outfit"];

/**
 * The combat sets the game names, by the ids the character sprite catalogue gives them: sword and shield
 * (`one-hand`), bow and spear.
 */
export const COMBAT_SETS = ["one-hand", "bow", "spear"] as const;
export type CombatSet = (typeof COMBAT_SETS)[number];

/**
 * The combat sets whose weapons the game ships (ADR-094 decision 18): sword and shield alone. A piece a player may
 * choose covers every one of them, so adding a set here is refused by the validator while any `creator` piece cannot
 * play it.
 */
export const ENABLED_SETS: readonly CombatSet[] = ["one-hand"];

/**
 * A style: the pack's code for an item, 1 to 16 characters of `a-z` and `0-9` starting with a letter.
 */
export const LOOK_STYLE_PATTERN = /^[a-z][a-z0-9]{0,15}$/;

/**
 * The largest colour number a piece may carry.
 */
export const MAX_LOOK_COLOUR = 99;

/**
 * A piece's key: its style, a hyphen and its colour written without leading zeros, as `humn-3` or `bob1-13`. The
 * packs' colour 0 is their recolour template and no piece of the shipped table, though the pattern admits a 0. The
 * database's shape check of a stored key is this pattern.
 */
export const LOOK_KEY_PATTERN = /^[a-z][a-z0-9]{0,15}-(?:0|[1-9][0-9]?)$/;

/**
 * The style of a piece that is nothing: no art, no sheet, no frame. A slot a player may leave empty holds such a piece
 * in the table, so that "none" is a key like any other wherever a look is stored, signed, sent and checked, and has a
 * number of its own on the wire, distinct from the 0 an unknown piece falls back to. It is the character viewer's
 * `none`, the word its address writes for an empty slot.
 */
export const BARE_STYLE = "none";

/**
 * The one colour a bare piece carries: nothing has no colours, so one key names it.
 */
export const BARE_COLOUR = 1;

/**
 * The key of the hair that is none: what a character with no hair holds in its hair field.
 */
export const NO_HAIR_KEY = `${BARE_STYLE}-${BARE_COLOUR}`;

/**
 * The slots whose piece may be bare: the hair alone. A skin and an outfit are always drawn (a character with nothing
 * over its body is drawn in the floor outfit), and a hat or a hand left off is no piece at all.
 */
export const BARE_SLOTS: readonly LookSlot[] = ["hair"];

/**
 * The most characters a key holds: 16 of style, the hyphen and 2 of colour.
 */
export const MAX_LOOK_KEY_CHARS = 19;

/**
 * The most pieces the table holds: a piece's number travels as a `u16` and 0 is "nothing".
 */
export const MAX_LOOK_PIECES = 65_535;

/**
 * One piece of a character's look.
 */
export interface LookPiece {
  readonly key: string;
  readonly slot: LookSlot;
  readonly style: string;
  readonly colour: number;
  readonly label: string;
  readonly sets: readonly CombatSet[];
  readonly creator: boolean;
}

const LOOK_FIELDS = ["key", "slot", "style", "colour", "label", "sets", "creator"] as const;

/**
 * Whether a key names a bare piece: its style is `BARE_STYLE`. Read from the key alone, so a rule that knows no table
 * can ask it.
 *
 * @param key A piece key.
 * @returns True for a key of the bare style, as `none-1`.
 * @example
 * isBareKey("none-1");
 * // true
 */
export function isBareKey(key: string): boolean {
  return key.startsWith(`${BARE_STYLE}-`);
}

/**
 * Whether a piece is bare: nothing is drawn for it, it has no sheet, and no pack holds it.
 *
 * @param piece The piece.
 * @returns True for a piece of the bare style.
 */
export function isBarePiece(piece: Pick<LookPiece, "style">): boolean {
  return piece.style === BARE_STYLE;
}

/**
 * The name a piece goes by in its problems: its key when that is well formed, else its place. The field is read
 * through its descriptor, so no getter runs.
 *
 * @param value The record.
 * @param index Its place in the table.
 * @returns The key, or `#n` for the n-th record.
 */
function pieceName(value: unknown, index: number): string {
  const named: unknown = isPlainRecord(value) ? Object.getOwnPropertyDescriptor(value, "key")?.value : undefined;
  return typeof named === "string" && LOOK_KEY_PATTERN.test(named) ? named : `#${index + 1}`;
}

/**
 * Reads a string that matches a pattern.
 *
 * @param place Where to report.
 * @param value The value.
 * @param path Its path.
 * @param pattern The pattern.
 * @param what What such a string is, for `is not`.
 * @returns The string, or null when it is missing or wrong.
 */
function patterned(place: Place, value: unknown, path: string, pattern: RegExp, what: string): string | null {
  if (value === undefined) {
    return null;
  }
  if (typeof value !== "string") {
    report(place, `${path} is not a string`);
    return null;
  }
  if (!pattern.test(value)) {
    report(place, `${path} ${value} is not ${what}`);
    return null;
  }
  return value;
}

/**
 * Reads the combat sets a piece covers: a list of members of `COMBAT_SETS`, none twice.
 *
 * @param place Where to report.
 * @param value The value.
 * @returns The sets, or null when the list or any member is wrong.
 */
function setsOf(place: Place, value: unknown): CombatSet[] | null {
  const list = listOf(place, value, "sets");
  if (list === null) {
    return null;
  }
  const sets: CombatSet[] = [];
  let sound = true;
  for (const [index, member] of list.entries()) {
    const set = oneOf(place, member, `sets[${index}]`, COMBAT_SETS, "a combat set");
    if (set === null) {
      sound = false;
    } else if (sets.includes(set)) {
      report(place, `sets[${index}] names ${set} twice`);
      sound = false;
    } else {
      sets.push(set);
    }
  }
  return sound ? sets : null;
}

/**
 * Checks what `creator` asks of a piece (ADR-094 decisions 18 and 26): a piece a player may choose is a skin, a hair
 * or an outfit, and covers every enabled set.
 *
 * @param place Where to report.
 * @param slot The piece's slot, null when it was wrong.
 * @param sets The sets it covers, null when they were wrong.
 * @param enabledSets The sets whose weapons the game ships.
 * @returns Whether the piece may be a player's.
 */
function creatorHolds(place: Place, slot: LookSlot | null, sets: readonly CombatSet[] | null, enabledSets: readonly CombatSet[]): boolean {
  let holds = true;
  if (slot !== null && !APPEARANCE_SLOTS.includes(slot)) {
    report(place, `creator is true for a ${slot} piece; a player chooses skin, hair and outfit pieces only (ADR-094 decision 26)`);
    holds = false;
  }
  if (sets !== null) {
    for (const set of enabledSets) {
      if (!sets.includes(set)) {
        report(place, `creator is true, and the piece does not cover the enabled set ${set}; a piece a player may choose covers every enabled set (ADR-094 decision 18)`);
        holds = false;
      }
    }
  }
  return holds;
}

/**
 * Checks what the bare style asks of a piece: it is a hair, in the one colour, and covers every combat set, since it
 * has no frame to lack, so enabling a set never strands a character with no hair.
 *
 * @param place Where to report.
 * @param slot The piece's slot, null when it was wrong.
 * @param colour Its colour, null when it was wrong.
 * @param sets The sets it covers, null when they were wrong.
 * @returns Whether the piece has the bare piece's one form.
 */
function bareHolds(place: Place, slot: LookSlot | null, colour: number | null, sets: readonly CombatSet[] | null): boolean {
  let holds = true;
  if (slot !== null && !BARE_SLOTS.includes(slot)) {
    report(place, `the style ${BARE_STYLE} is no piece at all, and only a hair may be ${BARE_STYLE}: a skin and an outfit are always drawn, and a hat or a hand left off is no piece`);
    holds = false;
  }
  if (colour !== null && colour !== BARE_COLOUR) {
    report(place, `a piece of the style ${BARE_STYLE} has colour ${BARE_COLOUR}, since nothing has no colours`);
    holds = false;
  }
  if (sets !== null) {
    const omitted = COMBAT_SETS.filter((set) => !sets.includes(set));
    if (omitted.length > 0) {
      report(place, `a piece of the style ${BARE_STYLE} covers every combat set, since it has no frame to lack; it omits ${omitted.join(", ")}`);
      holds = false;
    }
  }
  return holds;
}

/**
 * Checks one piece.
 *
 * @param place Where to report.
 * @param record The record.
 * @param enabledSets The sets whose weapons the game ships.
 * @returns The piece, built from its checked fields, or null.
 */
function pieceOf(place: Place, record: Readonly<Record<string, unknown>>, enabledSets: readonly CombatSet[]): LookPiece | null {
  const fields = fieldsOf(place, record, "", LOOK_FIELDS, "a look piece") as Readonly<Record<string, unknown>>;
  const key = patterned(place, fields["key"], "key", LOOK_KEY_PATTERN, "a style of 1 to 16 characters of a-z and 0-9 starting with a letter, a hyphen and a colour from 0 to 99");
  const slot = oneOf(place, fields["slot"], "slot", LOOK_SLOTS, "a look slot");
  const style = patterned(place, fields["style"], "style", LOOK_STYLE_PATTERN, "1 to 16 characters of a-z and 0-9 starting with a letter");
  const colour = whole(place, fields["colour"], "colour", 0, MAX_LOOK_COLOUR);
  let joined = true;
  if (key !== null && style !== null && colour !== null && key !== `${style}-${colour}`) {
    report(place, `key is ${key}, and it must be its style and its colour, ${style}-${colour}`);
    joined = false;
  }
  const label = name(place, fields["label"], "label");
  const sets = setsOf(place, fields["sets"]);
  const creator = flag(place, fields["creator"], "creator");
  const mayBeChosen = creator !== true || creatorHolds(place, slot, sets, enabledSets);
  const bare = style !== BARE_STYLE || bareHolds(place, slot, colour, sets);
  if (!bare || key === null || slot === null || style === null || colour === null || !joined || label === null || sets === null || creator === null || !mayBeChosen || Object.keys(fields).length !== LOOK_FIELDS.length) {
    return null;
  }
  return { key, slot, style, colour, label, sets, creator };
}

/**
 * Checks each record of the looks table in turn: a value that is not JSON data first, then the record's own fields,
 * then that its key is not held twice; and the table as a whole: no more pieces than a `u16` numbers, and a skin, a
 * hair and an outfit a player may choose, each with art, so a character can always be given a look and a default
 * look never has to be the hair that is none.
 *
 * @param records The looks table.
 * @param problems Where problems go.
 * @param enabledSets The sets whose weapons the game ships; `ENABLED_SETS` unless a test hands in another.
 * @returns Each piece that passed, in the table's order, a second of one key left out.
 */
export function checkLooks(records: readonly unknown[], problems: RulesProblem[], enabledSets: readonly CombatSet[] = ENABLED_SETS): LookPiece[] {
  const passed: LookPiece[] = [];
  const seen = new Set<string>();
  const table: Place = { table: "looks", id: null, problems };
  if (records.length > MAX_LOOK_PIECES) {
    report(table, `the table holds ${records.length} pieces, and a piece's number is a u16: at most ${MAX_LOOK_PIECES}`);
  }
  for (const [index, value] of records.entries()) {
    const recordId = pieceName(value, index);
    const place: Place = { table: "looks", id: recordId, problems };
    const foreign = foreignValue(value, "");
    if (foreign !== null) {
      report(place, foreignMessage(foreign, "the record"));
      continue;
    }
    if (!isPlainRecord(value)) {
      report(place, "it is not a record of fields");
      continue;
    }
    const built = pieceOf(place, value, enabledSets);
    if (!recordId.startsWith("#") && seen.has(recordId)) {
      report(place, "the key is held twice");
      continue;
    }
    seen.add(recordId);
    if (built !== null) {
      passed.push(built);
    }
  }
  for (const slot of APPEARANCE_SLOTS) {
    if (!passed.some((piece) => piece.slot === slot && piece.creator && !isBarePiece(piece))) {
      report(table, `the table holds no ${slot} piece a player may choose; a character needs a skin, a hair and an outfit`);
    }
  }
  return passed;
}
